import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Transition } from '@headlessui/react';
import ThemeToggle from '../global/ThemeToggle';
import { Link, useLocation } from 'react-router-dom';
import client, { urlFor } from '../../config/sanityClient';
import { useTheme } from '../../context/ThemeContext';
import localLogo from '../../assets/eien_logo.png';
import WhatsAppLink from '../global/WhatsAppLink';
import { CTA_IDS, CTA_LOCATIONS } from '../../lib/analytics.constants';

const navigation = [
  { name: 'Inicio', href: '/' },
  { name: 'Modelos', href: '/modelos' },
  { name: 'Conócenos', href: '/conocenos' },
  { name: 'Capacidades', href: '/capacidades' },
];

export default function Header({ onContactClick, onAboutUsClick }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkBackground, setIsDarkBackground] = useState(false);
  const [logo, setLogo] = useState(null);
  const location = useLocation();
  const { isDarkMode } = useTheme();
  // Si Sanity no responde (CORS, red), cae al asset local: el logo nunca desaparece
  const logoSrc = logo ? urlFor(logo).url() : localLogo;

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const query = `*[_type == "webImages"][0].logo`;
        const result = await client.fetch(query);
        setLogo(result);
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };

    fetchLogo();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);

        // Check if the scroll is over any dark section
        const blackSections = document.querySelectorAll('.black-bg-section');
        let isInDarkSection = false;

        blackSections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            // Check if the section is partially visible
            if (rect.top < window.innerHeight * 0.1 && rect.bottom > window.innerHeight * 0.1) {
                isInDarkSection = true;
            }
        });

        setIsDarkBackground(isInDarkSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
        window.removeEventListener('scroll', handleScroll);
    };
}, []);


  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleMobileContactClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    // Primero ejecutamos onContactClick para preparar cualquier estado necesario
    onContactClick();

    // Luego hacemos el scroll con un pequeño delay para asegurar que todo esté listo
    setTimeout(() => {
      const footerElement = document.getElementById('footer');
      if (footerElement) {
        footerElement.scrollIntoView({ behavior: 'smooth' });

        // Aseguramos que se mantenga en la posición del footer
        const checkScroll = setInterval(() => {
          const rect = footerElement.getBoundingClientRect();
          if (rect.top <= window.innerHeight) {
            clearInterval(checkScroll);
          } else {
            footerElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);

        // Limpiamos el interval después de 1 segundo para evitar que se ejecute indefinidamente
        setTimeout(() => clearInterval(checkScroll), 1000);
      }
    }, 100);
  };

  // Check if link is active
  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 mx-auto mt-6 border transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] z-40 ${
        isScrolled
          ? 'w-[78%] md:w-[68%] backdrop-blur-md bg-white/80 dark:bg-black/70 shadow-sm dark:shadow-black/40 border-gray-200/50 dark:border-white/[0.08]'
          : 'w-full bg-transparent border-transparent'
        }`}
      style={{ borderRadius: isScrolled ? '20px' : '0' }}
    >
      <nav aria-label="Global" className="mx-auto max-w-7xl p-2 md:px-8">
        {/* Mobile Layout */}
        <div className="flex md:hidden items-center justify-between relative">
          <Link to="/" className="-m-1.5 p-1.5 flex items-center flex-shrink-0">
            {!mobileMenuOpen && <span className="sr-only ignore-translation">eien</span>}
            <img
              alt="eien"
              src={logoSrc}
              className="h-8 shrink-0"
              loading='lazy'
            />
          </Link>
          {/* Translator centered */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <div className="elfsight-app-b9661c81-6933-4f67-9c18-85a314ae8294" data-elfsight-app-lazy></div>
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 transition-colors duration-200 flex-shrink-0 ml-auto text-gray-700 dark:text-gray-200"
            aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className={`block h-0.5 w-full bg-current rounded-full transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${mobileMenuOpen ? 'rotate-45 translate-y-[9px]' : ''}`} />
              <span className={`block h-0.5 w-full bg-current rounded-full transition-all duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] ${mobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`} />
              <span className={`block h-0.5 w-full bg-current rounded-full transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${mobileMenuOpen ? '-rotate-45 -translate-y-[9px]' : ''}`} />
            </div>
          </button>
        </div>

        {/* Desktop/Tablet Layout - Grid for proper centering */}
        <div className="hidden md:grid grid-cols-[auto_1fr_auto] items-center gap-4">
          {/* Left: Logo + Name */}
          <div className="flex items-center">
            <Link to="/" className="sm:w-48 md:w-64 -m-1.5 p-1.5 flex items-center">
              {!mobileMenuOpen && <span className="sr-only ignore-translation">eien</span>}
              <img
                alt="eien"
                src={logoSrc}
                className="h-8 shrink-0"
                loading='lazy'
              />
              {!isScrolled && !mobileMenuOpen && (
                <h1 className="pl-3 text-xl font-bold flex items-center text-color ignore-translation whitespace-nowrap">eien</h1>
              )}
            </Link>
          </div>

          {/* Center: Navigation Links */}
          <div className="flex justify-center items-center gap-x-10">
            {navigation.map((item) => (
              item.name === 'Contacto' ? (
                <button
                  key={item.name}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileContactClick(e);
                  }}
                  className={`text-sm/6 font-semibold transition-colors duration-300 whitespace-nowrap ${
                    isDarkBackground || isDarkMode ? 'text-white hover:text-green-400' : 'text-gray-900 hover:text-green-500'
                  }`}
                >
                  {item.name}
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm/6 font-semibold transition-colors duration-300 whitespace-nowrap ${
                    isActive(item.href) 
                      ? 'text-green-500' 
                      : 'text-gray-700 dark:text-gray-300 hover:text-[#11b36d] dark:hover:text-[#11b36d]'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* Right: Elfsight Translator + Button + Theme Toggle */}
          <div className="flex items-center justify-end gap-3 flex-shrink-0">
            <div className="elfsight-app-b9661c81-6933-4f67-9c18-85a314ae8294" data-elfsight-app-lazy></div>
            <WhatsAppLink
              ctaId={CTA_IDS.HEADER_WHATSAPP}
              ctaLocation={CTA_LOCATIONS.HEADER}
              message="Hola, me interesa saber más sobre sus servicios"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full bg-[#11b36d] text-white shadow-[0_2px_6px_rgba(17,179,109,0.2),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#0ea060] hover:shadow-[0_3px_10px_rgba(17,179,109,0.3),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
            >
              Escribinos
            </WhatsAppLink>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="relative z-50 lg:hidden"
      >
        <Transition.Child
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/10 dark:bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="fixed inset-x-0 top-0 overflow-hidden">
              <Transition.Child
                as={DialogPanel}
                enter="transform transition ease-out duration-300"
                enterFrom="-translate-y-full"
                enterTo="translate-y-0"
                leave="transform transition ease-in duration-300"
                leaveFrom="translate-y-0"
                leaveTo="-translate-y-full"
                className="w-full bg-white/[0.97] dark:bg-[#080808]/[0.97] backdrop-blur-2xl overflow-y-auto min-h-[100dvh]"
              >
                <style>{`
                  @keyframes navReveal {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                  }
                  .nav-reveal { animation: navReveal 0.4s cubic-bezier(0.32,0.72,0,1) both; }
                `}</style>

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/[0.07]">
                  <Link to="/" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
                    <img
                      alt="eien"
                      src={logoSrc}
                      className="h-8 w-auto"
                      loading='lazy'
                    />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className="-m-2.5 rounded-md p-2.5 text-gray-500 dark:text-gray-400 transition-colors duration-200"
                    aria-label="Cerrar menú"
                  >
                    <div className="w-5 h-5 relative">
                      <span className="absolute top-1/2 left-0 w-full h-0.5 bg-current rounded-full rotate-45 -translate-y-1/2" />
                      <span className="absolute top-1/2 left-0 w-full h-0.5 bg-current rounded-full -rotate-45 -translate-y-1/2" />
                    </div>
                  </button>
                </div>

                <div className="px-6 py-8 space-y-1">
                  {navigation.map((item, idx) => (
                    item.name === 'Contacto' ? (
                      <button
                        key={item.name}
                        onClick={handleMobileContactClick}
                        className="nav-reveal block w-full px-4 py-3.5 text-left text-lg font-medium text-gray-900 dark:text-white rounded-xl hover:bg-[#11b36d]/[0.07] hover:text-[#11b36d] transition-colors duration-200"
                        style={{ animationDelay: `${idx * 55 + 60}ms` }}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`nav-reveal block w-full px-4 py-3.5 text-lg font-medium rounded-xl transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'text-[#11b36d] bg-[#11b36d]/[0.07]'
                            : 'text-gray-900 dark:text-white hover:bg-[#11b36d]/[0.07] hover:text-[#11b36d]'
                        }`}
                        style={{ animationDelay: `${idx * 55 + 60}ms` }}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                  <div className="nav-reveal mt-8 pt-6 border-t border-gray-100 dark:border-white/[0.07] space-y-3" style={{ animationDelay: '300ms' }}>
                    <WhatsAppLink
                      ctaId={CTA_IDS.MOBILE_MENU_WHATSAPP}
                      ctaLocation={CTA_LOCATIONS.MOBILE_MENU}
                      message="Hola, me interesa saber más sobre sus servicios"
                      className="group block w-full text-center px-5 py-3 text-sm font-semibold rounded-full bg-[#11b36d] text-white shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#0ea060] active:scale-[0.97] transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Escribinos
                    </WhatsAppLink>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Tema:</span>
                      <ThemeToggle />
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </header>
  );
}
